import React, { useState, useEffect } from "react";
import { ThemeProvider, CssBaseline } from "@mui/material";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { lightTheme, darkTheme } from "./theme/theme";
import Navigation from "./components/Navigation/Navigation";
import Footer from "./components/Footer/Footer";
import Home from "./pages/Home/Home";
import About from "./pages/About";
import Services from "./pages/Services";
import Portfolio from "./pages/Portfolio";
import ProjectDetail from "./pages/ProjectDetail";
import Contact from "./pages/Contact";
import Imprint from "./pages/Imprint";
import Privacy from "./pages/Privacy";
import CookieNotice from "./components/CookieNotice/CookieNotice";
import { initGA, trackPageView } from "./services/analytics";

// Aktuelle Version der Cookie-Einstellungen
const COOKIE_SETTINGS_VERSION = "1.0";

// Überprüfe, ob ein Cookie-Reset erforderlich ist
// Diese Funktion wird nur einmalig ausgeführt, wenn die Version nicht übereinstimmt
const checkAndResetCookieSettings = () => {
  const currentVersion = localStorage.getItem('cookieSettingsVersion');
  
  // Wenn keine Version gespeichert ist oder die Version veraltet ist,
  // lösche die Cookie-Einstellungen und setze die neue Version
  if (currentVersion !== COOKIE_SETTINGS_VERSION) {
    localStorage.removeItem('cookiesAccepted');
    localStorage.removeItem('cookieSettings');
    localStorage.setItem('cookieSettingsVersion', COOKIE_SETTINGS_VERSION);
  }
};

// Führe die Funktion einmalig aus
checkAndResetCookieSettings();

// Initialisiere Google Analytics
initGA();

// Scroll to top component
const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    // Tracke Seitenaufruf bei Routenwechsel
    trackPageView(pathname);
  }, [pathname]);

  return null;
};

function App() {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedMode = localStorage.getItem("darkMode");
    return savedMode ? JSON.parse(savedMode) : false;
  });

  useEffect(() => {
    localStorage.setItem("darkMode", JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  return (
    <HelmetProvider>
      <ThemeProvider theme={isDarkMode ? darkTheme : lightTheme}>
        <CssBaseline />
        <Router>
          <ScrollToTop />
          <Navigation isDarkMode={isDarkMode} toggleTheme={toggleTheme} />
          <Routes>
            <Route
              path="/"
              element={
                <>
                  <Home />
                </>
              }
            />
            <Route
              path="/about"
              element={
                <>
                  <About />
                </>
              }
            />
            <Route
              path="/services"
              element={
                <>
                  <Services />
                </>
              }
            />
            <Route
              path="/portfolio"
              element={
                <>
                  <Portfolio />
                </>
              }
            />
            <Route
              path="/portfolio/:id"
              element={
                <>
                  <ProjectDetail />
                </>
              }
            />
            <Route
              path="/contact"
              element={
                <>
                  <Contact />
                </>
              }
            />
            <Route
              path="/imprint"
              element={
                <>
                  <Imprint />
                </>
              }
            />
            <Route
              path="/privacy"
              element={
                <>
                  <Privacy />
                </>
              }
            />
            <Route path="*" element={<Home />} />
          </Routes>
          <CookieNotice />
          <Footer />
        </Router>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
